import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The twelfth written page, and the first one whose closing quote was already
// in hand. Everything below comes from the three-part full-page capture of
// digitopia.co/customers/netafim/ sent on 2026-08-28 — "tastamam şekilde,
// aynısını, tıpkısını tasarla" — with the two photographs supplied alongside
// it: the tobacco field the reference runs in the header, and the terraced
// paddies it runs under the opening.
//
// Its shape is the reference's: no grey panels below the header and no lists,
// because the reference runs all five parts of the story as left-ranged prose
// on the white page. "About Netafim" is a centred section rather than an
// `intro` band for the same reason — the reference sets that opening on the
// white page under its own heading, with the picture inside it, which is
// Arzum's and LC Waikiki's shape rather than the template's usual grey
// standfirst.
//
// The closing quote is not repeated as a `section`: `content/testimonials.ts`
// has held Benny Klener's words keyed to this slug since the Hall of Fame
// shipped — Netafim's card mark and his 900×900 portrait included — and
// `StoryBody` draws that card under the story. That is the testimonial
// template the request asked for, and it needed no new media: the portrait
// sent with this request is 600×600 in colour against a 900×900 cut into the
// monochrome the whole portrait set shares, so the file already here is the
// better one. The reference's wording of the quote differs from ours in two
// words; see the check.
export const added = story('netafim', 'Netafim', {
  lede: ['“Do more with less” applies also to operational excellence.'],
  heroPhoto: {
    src: '/photo/netafim-tobacco-field.webp',
    alt: 'A broadleaf tobacco field at dusk, rows of heavy green leaves running back to a '
      + 'tree line under a sky of lit cloud.',
  },
  sections: [
    {
      heading: 'About Netafim',
      centred: true,
      body: [
        'We’re farmers first and innovators second. We started in 1965, in the Negev desert in '
        + 'Israel, trying to grow crops in desert soil. So we know what it’s like to farm in '
        + 'extreme conditions. That struggle taught us how to combine precision irrigation, '
        + 'agronomic expertise and relentless innovation to help farmers grow more of any crop, '
        + 'in any climate, with less.',
      ],
      photo: {
        src: '/photo/netafim-rice-terraces.webp',
        alt: 'Terraced paddies in bright green running to a wooded mountain ridge, two figures '
          + 'on a path through them, the sun rising over the shoulder of the hill.',
      },
    },
    {
      heading: 'The Need?',
      body: [
        'Netafim is the global leader in irrigation technologies. By optimizing the way water '
        + 'is used, they not just improve the yield but also save the planet. They are '
        + 'relentlessly perfecting their products and solutions, to serve their customers even '
        + 'better and fulfill their vision. To be able to do so, operational excellence has '
        + 'become an integer part of their culture. They also strongly believe that digital '
        + 'transformation is an important component and enabler of operational excellence. So, '
        + 'why not combine the “best of both worlds”?',
      ],
    },
    {
      heading: 'The Solution',
      body: [
        'Together with our partner Programa Consulting we delivered an “Operations Digital '
        + 'Maturity Index” service. Looking at Netafim’s supply chain, logistics, '
        + 'manufacturing, procurement, and related processes from an operational excellence and '
        + 'digital maturity perspective, gave a great insight where they are and how they '
        + 'compare against peers.',
        'The amazing Programa Consulting team conducted one-on-one interviews, hosted '
        + 'workshops, moderated lively discussions, analyzed all collected data, and compiled a '
        + 'very powerful report. Now Netafim’s top management knows where they are and what '
        + 'they must focus on.',
      ],
    },
    {
      heading: 'The Benefits',
      body: [
        'Netafim is a very advanced organization with a very high degree of professionalism and '
        + 'management capabilities. Now they know their digital maturity scores, which they did '
        + 'not knew before. Based on this insight and in-depth analysis, they can take more '
        + 'precise decisions, prioritize their investments, harmonize their operations in '
        + 'various countries, and govern the digital transformation much better. In this sense, '
        + 'digital will empower and streamline their operational excellence endeavour.',
      ],
    },
    {
      heading: 'The Next Steps',
      body: [
        'Netafim will continue to work on their operational excellence roadmap. Now they added '
        + 'substantial investments in terms of digital transformation to the portfolio. They '
        + 'will continue to monitor the progress and leverage everything at their hands to make '
        + 'the transformation successful. In Netafim’s case this is not just a business '
        + 'objective, but a mission to “grow more with less” to save the planet.',
      ],
    },
    {
      heading: 'About Programa',
      body: [
        'Programa is an international management consultancy and engineering company '
        + 'specializing in supply chain management, logistics, materials handling, modeling & '
        + 'analytics. As partners we are adding value to our customers business by developing '
        + 'and implementing significant cost effective tangible improvements thus achieving a '
        + 'competitive advantage in their market place.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a three-part full-page capture of '
    + `${STORY_SOURCE}/netafim/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production. Four things in it are the '
    + 'reference’s own and were left verbatim rather than silently corrected in what is '
    + 'partly a customer’s own voice: “they not just improve the yield”, “an integer part of '
    + 'their culture” (the sense is *integral*), “which they did not knew before”, and '
    + '“endeavour” in British spelling on a page that otherwise sets “optimizing” and '
    + '“analyzed”. One thing to decide rather than check: the quote card at the foot is the '
    + 'Hall of Fame template as asked for, and it takes its wording from digitopia.co’s own '
    + 'Testimonials page — “Programa utilizing Digitopia **platform** conducted an '
    + '**Operations Digital Maturity Index (OMI)** analysis…” — where this story page reads '
    + '“Digitopia **technology**… an **OMI** analysis”. Two of their pages quote Mr Klener '
    + 'slightly differently; the Testimonials wording won because it is the version we hold '
    + 'as a file and it is shared with the Hall of Fame, so changing it changes both. His '
    + 'name and title are identical on the two. Two deliberate differences of shape from the '
    + 'reference, each a template-wide decision rather than this page’s: the header eyebrow '
    + 'reads “Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS STORIES”, '
    + 'and this story has no PDF, so there is no “Read the full story” button — it was not '
    + 'among the sixteen documents sent on 2026-08-27. One production note: both photographs '
    + 'are small originals — 768×432 and 1200×455, which is what the live site serves — so '
    + 'they are drawn at that width and will look soft on a high-density screen until larger '
    + 'files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 19
